import { Children, isValidElement, ReactElement, ReactNode } from "react";
import { SteppedModalStep, SteppedModalStepProps } from "./stepped-modal-step";
import { SteppedModalFooter } from "./stepped-modal-footer";
import { SteppedModalFormikStep, SteppedModalFormikStepProps } from "./stepped-modal-formik-step";
import type { IStepSchema } from "./stepped-modal.types";

/**
 * Structural check for `IStepSchema`, used to tell a genuine schema apart from a malformed
 * `validation` prop (e.g. `null` from an untyped caller, or a plain object missing `isValidSync`).
 *
 * Both members are checked, not just `isValidSync`: the modal calls `isValidSync` to gate a step and
 * `concat` to compose the whole-form schema, so an object carrying only the former would pass this
 * guard and then throw during composition — crashing instead of failing closed.
 * @param value Candidate `validation` prop value.
 * @returns Whether the value can be used as a step schema.
 */
export const isStepSchema = (value: unknown): value is IStepSchema =>
    typeof value === "object" &&
    value !== null &&
    typeof (value as IStepSchema).isValidSync === "function" &&
    typeof (value as IStepSchema).concat === "function";

/**
 * Extract the SteppedModal.Step children so the header stepper and body can be derived from them.
 */
export const getSteps = (children: ReactNode): ReactElement<SteppedModalStepProps>[] =>
    Children.toArray(children).filter(
        (child): child is ReactElement<SteppedModalStepProps> =>
            isValidElement(child) && child.type === SteppedModalStep
    );

/**
 * Extract the SteppedModal.Formik.Step children. Separate from `getSteps` because the Formik variant's
 * step requires a `validation` prop that the base variant has no use for.
 */
export const getFormikSteps = (children: ReactNode): ReactElement<SteppedModalFormikStepProps>[] =>
    Children.toArray(children).filter(
        (child): child is ReactElement<SteppedModalFormikStepProps> =>
            isValidElement(child) && child.type === SteppedModalFormikStep
    );

/**
 * Extract the SteppedModal.Footer child (if any) so it can be rendered beneath the body.
 */
export const getFooter = (children: ReactNode): ReactNode =>
    Children.toArray(children).find((child) => isValidElement(child) && child.type === SteppedModalFooter);

/**
 * Clamp a (possibly out-of-range) active step index to a valid step. Returns 0 when there are no
 * steps, otherwise the nearest valid index in [0, stepsCount - 1]. This guards the body from
 * rendering `undefined` and the Mantine Stepper from receiving an invalid index.
 */
export const clampStepIndex = (active: number, stepsCount: number): number =>
    stepsCount === 0 ? 0 : Math.min(Math.max(active, 0), stepsCount - 1);
